import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      nav(loc.state?.from || "/dashboard");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ec-bg" data-testid="login-page">
      {/* Left: Form */}
      <div className="flex flex-col px-6 sm:px-10 py-10 lg:p-16">
        <Link to="/" className="w-fit">
          <Logo size={34} />
        </Link>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            <div className="eyebrow">Sign in</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3">
              Welcome back.
            </h1>
            <p className="text-ec-ink-soft mt-3">
              Continue managing your Professional Passport.
            </p>

            <form className="mt-10 space-y-5" onSubmit={onSubmit} data-testid="login-form">
              <div>
                <Label htmlFor="email" className="eyebrow">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="eyebrow">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 rounded-sm h-11 border-ec-line focus-visible:ring-ec-ink"
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <div className="text-sm text-ec-error border border-ec-error/30 bg-ec-error/5 px-3 py-2 rounded-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 rounded-sm bg-ec-ink hover:bg-zinc-800 gap-2"
                data-testid="login-submit-button"
              >
                {loading ? "Signing in…" : "Sign in"} <ArrowUpRight className="w-4 h-4" />
              </Button>
            </form>

            <p className="mt-8 text-sm text-ec-ink-soft">
              No EthicCred ID yet?{" "}
              <Link to="/register" className="font-semibold text-ec-ink underline underline-offset-4 hover:text-ec-emerald" data-testid="login-to-register-link">
                Claim your Passport
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right: Visual */}
      <div className="hidden lg:flex bg-ec-ink text-white p-16 relative overflow-hidden">
        <div className="absolute -right-32 -top-32 w-96 h-96 border border-white/10 rounded-full" />
        <div className="absolute -right-52 -top-52 w-[36rem] h-[36rem] border border-white/10 rounded-full" />
        <div className="relative self-end max-w-md">
          <div className="eyebrow text-zinc-400">EthicCred · 2026</div>
          <p className="font-display text-3xl font-bold mt-4 leading-tight">
            "Your career story is the most valuable asset you'll ever build. We just made it
            tamper-proof."
          </p>
          <div className="mt-8 inline-flex items-center gap-2 text-xs eyebrow text-ec-emerald border border-ec-emerald/30 bg-ec-emerald/10 px-3 py-1 rounded-full">
            <span className="w-1.5 h-1.5 bg-ec-emerald rounded-full" />
            2.4M verified passports
          </div>
        </div>
      </div>
    </div>
  );
}
